/**
 * Per Ankh — the bot check on public forms, in the browser (Cloudflare Turnstile).
 *
 * The pages' form scripts are classic scripts, so this module hangs one small
 * object on window:
 *
 *   PerAnkhBotCheck.mount(container)  draw the check into an element (once)
 *   await PerAnkhBotCheck.headers()   { 'X-Turnstile-Token': … } to add to a fetch, or {}
 *   PerAnkhBotCheck.reset()           after each submission; a token works only once
 *
 * The site key comes from GET /api/bot-check. When the server has no keys it
 * answers null, nothing is drawn, and headers() returns {} — the forms behave
 * exactly as they did before the check existed. The server is the real gate
 * (server/lib/bot-check.js); this only collects the token.
 */

const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const TOKEN_WAIT_MS = 15000;

let configPromise = null;
let scriptPromise = null;
let widgetId = null;
let token = '';
let waiters = [];

function siteKey() {
  configPromise ||= fetch('/api/bot-check', { headers: { Accept: 'application/json' } })
    .then((r) => (r.ok ? r.json() : {}))
    .then((d) => d.siteKey || null)
    .catch(() => null);
  return configPromise;
}

function loadScript() {
  scriptPromise ||= new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = SCRIPT_URL;
    s.async = true;
    s.onload = () => resolve(window.turnstile);
    s.onerror = () => reject(new Error('The security check could not load.'));
    document.head.appendChild(s);
  });
  return scriptPromise;
}

function settle(value) {
  token = value;
  const pending = waiters;
  waiters = [];
  pending.forEach((fn) => fn(value));
}

async function mount(container, { appearance = 'always' } = {}) {
  if (!container || widgetId !== null) return;
  const key = await siteKey();
  if (!key) return;
  try {
    const turnstile = await loadScript();
    widgetId = turnstile.render(container, {
      sitekey: key,
      theme: 'dark',
      appearance,
      callback: (t) => settle(t),
      'expired-callback': () => { token = ''; },
      'error-callback': () => { token = ''; },
    });
  } catch (err) {
    console.warn('[BotCheck]', err.message);
  }
}

/** Headers carrying a fresh token. Waits briefly while the check is still running. */
async function headers() {
  const key = await siteKey();
  if (!key) return {};
  if (!token && widgetId !== null) {
    await new Promise((resolve) => {
      waiters.push(resolve);
      setTimeout(() => resolve(''), TOKEN_WAIT_MS);
    });
  }
  // Sent even when empty: the server then answers with a clear "try again".
  return { 'X-Turnstile-Token': token };
}

function reset() {
  token = '';
  if (widgetId !== null && window.turnstile) window.turnstile.reset(widgetId);
}

window.PerAnkhBotCheck = { mount, headers, reset };
